import React, { useEffect, useState } from 'react';
import { Modal, Select, Button, Form, message } from 'antd';
import { DataSourceTableType, JoinType } from '@/types/dataMap';
import { RelationshipDataConditions, RelationshipSubmitData } from '../../hooks/useRelationshipModal';

interface AddRelationshipModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (relationship: RelationshipSubmitData) => void;
  leftTable: TableData;
  rightTable: TableData;
  conditions?: RelationshipDataConditions[];
}

interface TableData {
  tableName: string;
  tableId?: string;
  fields: FieldData[];
}

interface FieldData {
  key: string;
  type: string;
  id?: string;
}

const AddRelationshipModal: React.FC<AddRelationshipModalProps> = ({ 
  visible, 
  onClose, 
  onConfirm, 
  leftTable, 
  rightTable,
  conditions: initialConditions
}) => {
  // 关联类型
  const [relationshipType, setRelationshipType] = useState<JoinType>('LEFT');
  // 关联条件列表
  const [conditions, setConditions] = useState<RelationshipDataConditions[]>([
      {
        id: `condition_${Date.now()}_0`,
        leftColumnId: leftTable.fields[0]?.id || '',
        leftColumnName: leftTable.fields[0]?.key || '',
        rightColumnId: rightTable.fields[0]?.id || '',
        rightColumnName: rightTable.fields[0]?.key || '',
        operator: 'EQUALS'
      }
    ]);
    const globalConfig = (window as any)?._intellix_global_config ?? {}
    
  useEffect(()=>{    
    if(initialConditions?.length) {
      setConditions(initialConditions.map((e)=> ({
        id: e.id,
        leftColumnId: e.leftColumnId,
        leftColumnName: e.leftColumnName,
        rightColumnId: e.rightColumnId,
        rightColumnName: e.rightColumnName,
        operator:  globalConfig.operatorEnum[e.operator]
      })))
    } else {
      // 当没有初始条件时，根据当前的leftTable和rightTable生成默认条件
      setConditions([
        {
          id: `condition_${Date.now()}_0`,
          leftColumnId: leftTable.fields[0]?.id || '',
          leftColumnName: leftTable.fields[0]?.key || '',
          rightColumnId: rightTable.fields[0]?.id || '',
          rightColumnName: rightTable.fields[0]?.key || '',
          operator: 'EQUALS'
        }
      ]);
    }
  },[initialConditions, leftTable, rightTable])

  // 添加条件
  const addCondition = () => {
    // 计算两个表中字段最少的数量
    const minFields = Math.min(leftTable.fields.length, rightTable.fields.length);
    
    // 检查当前条件数量是否已达最大值
    if (conditions.length >= minFields) {
      message.error('已达最大关联条件');
      return;
    }
    
    // 找到第一个未被使用的左侧字段
    const availableLeftField = leftTable.fields.find(field => {
      return !conditions.some(c => c.leftColumnId === field.id);
    });
    
    // 找到第一个未被使用的右侧字段
    const availableRightField = rightTable.fields.find(field => {
      return !conditions.some(c => c.rightColumnId === field.id);
    });
    
    const newCondition: RelationshipDataConditions = {
      id: `condition_${Date.now()}_${conditions.length}`,
      leftColumnId: availableLeftField?.id || '',
      leftColumnName: availableLeftField?.key || '',
      rightColumnId: availableRightField?.id || '',
      rightColumnName: availableRightField?.key || '',
      operator: 'EQUALS'
    };
    setConditions([...conditions, newCondition]);
  };

  // 删除条件
  const removeCondition = (id: string) => {
    if (conditions.length <= 1) return;
    setConditions(conditions.filter(condition => condition.id !== id));
  };

  // 更新左侧字段
  const updateleftColumnId = (conditionId: string, fieldKey: string) => {
    const field = leftTable.fields.find(f => f.key === fieldKey);
    console.log('updateleftColumnId', conditionId, fieldKey);
    
    setConditions(conditions.map(condition => {
      if (condition.id === conditionId) {
        return {
          ...condition,
          leftColumnId: field?.id || '',
          leftColumnName: fieldKey,
        };
      }
      return condition;
    }));
  };

  // 更新右侧字段
  const updaterightColumnId = (conditionId: string, fieldKey: string) => {
    const field = rightTable.fields.find(f => f.key === fieldKey);
    setConditions(conditions.map(condition => {
      if (condition.id === conditionId) {
        return {
          ...condition,
          rightColumnId: field?.id || '',
          rightColumnName: fieldKey,
        };
      }
      return condition;
    }));
  };

  // 更新条件类型
  const updateoperator = (conditionId: string, value: string) => {
    setConditions(conditions.map(condition => {
      if (condition.id === conditionId) {
        return { ...condition, operator: value };
      }
      return condition;
    }));
  };

  // 处理表单提交
  const handleSubmit = () => {
    // 检查是否所有条件都已填写
    const allConditionsFilled = conditions.every(condition => 
      condition.leftColumnId && condition.rightColumnId && condition.operator
    );
    
    if (!allConditionsFilled) {
      message.error('请填写所有关联条件');
      return;
    }    
    const relationship: RelationshipSubmitData = {
      leftTable: leftTable.tableName,
      leftTableId: leftTable.tableId,
      rightTable: rightTable.tableName,
      rightTableId: rightTable.tableId,
      relationshipType,
      conditions
    };
    onConfirm(relationship);
    onClose();
  };
  return (
    <Modal
      title=""
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
    >
      <div className="py-6 px-8">
        {/* 关联类型 */}
        <div className="mb-8">
          <div className="text-sm font-medium text-gray-700 mb-2">关联类型</div>
          <div className="flex items-center justify-between gap-4">
            <div className="bg-blue-100 text-blue-600 px-4 py-2 rounded">
              left Table：{leftTable.tableName}
            </div>
            <Select 
              value={relationshipType}
              onChange={setRelationshipType}
              style={{ width: 100 }}
            >
              <Select.Option value="LEFT">LEFT</Select.Option>
              <Select.Option value="RIGHT">RIGHT</Select.Option>
              <Select.Option value="INNER">INNER</Select.Option>
            </Select>
            <div className="bg-blue-100 text-blue-600 px-4 py-2 rounded">
              Right Table：{rightTable.tableName}
            </div>
          </div>
        </div>

        {/* 关联条件 */}
        <div className="mb-8">
          <div className="flex items-center justify-between text-sm font-medium text-gray-700 mb-2">
            <span>关联条件</span>
            <Button 
              type="dashed" 
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>} 
              onClick={addCondition}
            >
              添加条件
            </Button>
          </div>
          <div className="border border-gray-200 rounded-md py-4">
            <div className="flex items-center justify-between gap-4 mb-4">
              <div className="text-sm font-medium text-gray-700 w-[30%]">LEFT表所有的字段</div>
              <div className="text-sm font-medium text-gray-700 w-[15%] text-center">运算符</div>
              <div className="text-sm font-medium text-gray-700 w-[30%] text-right">RIGHT表所有的字段</div>
              <div className="text-sm font-medium text-gray-700 w-[25%] text-right">操作</div>
            </div>
            
            {/* 条件列表 - 设置最大高度和滚动 */}
            <div>
              {conditions.map((condition) => (
                <div key={condition.id} className="flex items-center justify-between gap-4 mb-4">
                  <Select 
                    value={condition.leftColumnName}
                    onChange={(value) => updateleftColumnId(condition.id, value)}
                    placeholder="选择LEFT表字段"
                    style={{ width: '30%' }}
                  >
                    {leftTable.fields.filter(field => {
                      // 过滤掉已经在其他条件中选择的左侧字段
                      return !conditions.some(c => c.id !== condition.id && c.leftColumnId === field.id);
                    }).map(field => (
                      <Select.Option key={field.key} value={field.key}>
                        {field.key}
                      </Select.Option>
                    ))}
                  </Select>
                  <Select 
                    value={condition.operator}
                    onChange={(value) => updateoperator(condition.id, value)}
                    style={{ width: '15%' }}
                  >
                    <Select.Option value="EQUALS">{'='}</Select.Option>
                    <Select.Option value="NOT_EQUALS">{'!='}</Select.Option>
                    <Select.Option value="GREATER_THAN">{'>'}</Select.Option>
                    <Select.Option value="LESS_THAN">{'<'}</Select.Option>
                    <Select.Option value="GREATER_THAN_OR_EQUAL">{'>='}</Select.Option>
                    <Select.Option value="LESS_THAN_OR_EQUAL">{'<='}</Select.Option>
                  </Select>
                  <Select 
                    value={condition.rightColumnName}
                    onChange={(value) => updaterightColumnId(condition.id, value)}
                    placeholder="选择RIGHT表字段"
                    style={{ width: '30%' }}
                  >
                    {rightTable.fields.filter(field => {
                      // 过滤掉已经在其他条件中选择的右侧字段
                      return !conditions.some(c => c.id !== condition.id && c.rightColumnId === field.id);
                    }).map(field => (
                      <Select.Option key={field.key} value={field.key}>
                        {field.key}
                      </Select.Option>
                    ))}
                  </Select>
                  <div className="w-[25%]">
                    {conditions.length > 1 && (
                      <Button 
                        type="text" 
                        danger
                        icon={
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          } 
                        onClick={() => removeCondition(condition.id)}
                        className="flex items-center justify-end w-full p-0"
                      >
                        删除
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex justify-end gap-3">
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" onClick={handleSubmit}>
            保存并退出
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddRelationshipModal;